import { cn } from "cn";
import type * as React from "react";

/**
 * Малый SVG-график «столбики и линия»: столбик на каждую точку и ломаная по
 * `line`. Где `line === null`, линия рвётся: сегменты через пропуск не
 * соединяются. Шкала общая — максимум по обоим рядам; отрицательные значения
 * рисуются как ноль. Оси и числа компонент не рисует, их даёт экран.
 * Размер задаёт контейнер (ширина 100 %, высота — `className`), `viewBox` —
 * по числу точек. Цвета — токены темы. Без хуков: чистая функция пропсов.
 */

const CHART_HEIGHT = 100;
/** Доля ширины ячейки, которую занимает столбик. */
const BAR_FILL = 0.8;

interface BarLinePoint {
  bar: number;
  line: number | null;
  /** Ключ точки; по умолчанию её индекс. */
  key?: string;
  /** Подпись для доступности: становится `<title>` столбика, если не задан форматер. */
  label?: string;
}

interface BarLineChartProps extends Omit<React.ComponentProps<"svg">, "children" | "points"> {
  points: readonly BarLinePoint[];
  /** Текст подсказки столбика; перекрывает `label` точки. */
  formatTitle?: (point: BarLinePoint, index: number) => string | undefined;
}

interface LinePoint {
  x: number;
  y: number;
}

function positive(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function toY(value: number, max: number): number {
  return max > 0 ? CHART_HEIGHT - (positive(value) / max) * CHART_HEIGHT : CHART_HEIGHT;
}

/** Непрерывные участки линии, разделённые `null`. */
function lineSegments(points: readonly BarLinePoint[], max: number): LinePoint[][] {
  const segments: LinePoint[][] = [];
  let current: LinePoint[] = [];
  points.forEach((point, index) => {
    if (point.line === null || !Number.isFinite(point.line)) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
      return;
    }
    current.push({ x: index + 0.5, y: toY(point.line, max) });
  });
  if (current.length > 0) {
    segments.push(current);
  }
  return segments;
}

function BarLineChart({
  points,
  formatTitle,
  className,
  "aria-label": ariaLabel,
  ...props
}: BarLineChartProps) {
  const max = points.reduce(
    (acc, point) =>
      Math.max(acc, positive(point.bar), point.line === null ? 0 : positive(point.line)),
    0,
  );
  const count = Math.max(points.length, 1);
  const offset = (1 - BAR_FILL) / 2;

  return (
    <svg
      data-slot="bar-line-chart"
      viewBox={`0 0 ${count} ${CHART_HEIGHT}`}
      preserveAspectRatio="none"
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      className={cn("block w-full overflow-visible", className)}
      {...props}
    >
      {points.map((point, index) => {
        const y = toY(point.bar, max);
        const title = formatTitle ? formatTitle(point, index) : point.label;
        return (
          <rect
            key={point.key ?? index}
            data-bar=""
            x={index + offset}
            y={y}
            width={BAR_FILL}
            height={CHART_HEIGHT - y}
            className="fill-muted-foreground/40"
          >
            {title ? <title>{title}</title> : null}
          </rect>
        );
      })}
      {lineSegments(points, max).map((segment) => {
        // Одиночная точка: дублируем её, чтобы круглый конец дал видимую точку.
        const drawn = segment.length === 1 ? [...segment, ...segment] : segment;
        return (
          <polyline
            key={segment[0]?.x}
            data-segment=""
            points={drawn.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            className="stroke-foreground"
          />
        );
      })}
    </svg>
  );
}

export { BarLineChart, type BarLinePoint };
